/**
 * Claude CLI Parser
 *
 * Parses Claude Code CLI commands for adding MCP servers.
 *
 * Supported syntaxes:
 * - claude mcp add [options] <name> <url>
 * - claude mcp add [options] <name> -- <command> [args...]
 * - claude mcp add-json <name> '<json>'
 *
 * Options:
 * - --transport / -t: stdio | http | sse
 * - --env / -e: KEY=VALUE (repeatable)
 * - --header / -H: "Header: Value" (repeatable)
 * - --scope: local | project | user (informational only)
 */

import { McpServerConfig } from '../../types';
import type { CliParseOptions, CliParseResult, ParsedCliFlags } from '../types';
import { createEmptyParsedFlags } from '../types';
import { BaseCliParser } from './base-parser';

class ClaudeCliParser extends BaseCliParser {
  readonly tool = 'claude' as const;

  readonly displayName = 'Claude Code';

  readonly prefixes = ['claude mcp add'];

  parse({ tokens }: CliParseOptions): CliParseResult {
    const warnings: string[] = [];

    // Handle add-json variant
    if (tokens[2] === 'add-json') {
      return this.parseAddJson(tokens.slice(3), warnings);
    }

    // Skip prefix tokens: claude, mcp, add
    const args = tokens.slice(3);
    if (args.length === 0) {
      return this.failResult('Missing server name and configuration');
    }

    const flags = ClaudeCliParser.parseFlags(args, warnings);
    return this.buildResult(flags, warnings);
  }

  /**
   * Parse the add-json variant: claude mcp add-json <name> '<json>'
   */
  private parseAddJson(args: string[], warnings: string[]): CliParseResult {
    if (args.length < 2) {
      return this.failResult('add-json requires a name and JSON configuration');
    }

    const name = args[0];
    // Join remaining args in case JSON was split
    const jsonStr = args.slice(1).join(' ');

    try {
      const config = JSON.parse(jsonStr) as McpServerConfig;
      return {
        success: true,
        servers: [{ name, config }],
        warnings: warnings.length > 0 ? warnings : undefined,
        tool: this.tool,
      };
    } catch (e) {
      return this.failResult(
        `Invalid JSON in add-json command: ${e instanceof Error ? e.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Parse flags and positional arguments from the command
   */
  private static parseFlags(args: string[], warnings: string[]): ParsedCliFlags {
    const flags = createEmptyParsedFlags();
    let i = 0;
    let sawDoubleDash = false;
    const positionalArgs: string[] = [];

    while (i < args.length) {
      const arg = args[i];

      if (sawDoubleDash) {
        // After --, everything is the command and its args
        if (!flags.command) {
          flags.command = arg;
        } else {
          flags.args.push(arg);
        }
        i += 1;
      } else if (arg === '--') {
        sawDoubleDash = true;
        i += 1;
      } else if (arg === '--transport' || arg === '-t') {
        // Transport flag
        i += 1;
        const value = args[i];
        if (value === 'stdio' || value === 'http' || value === 'sse') {
          flags.transport = value;
        } else {
          warnings.push(`Invalid transport type: ${value}. Using default.`);
        }
        i += 1;
      } else if (arg === '--env' || arg === '-e') {
        // Environment variable flag
        i += 1;
        const envVar = args[i];
        const parsed = BaseCliParser.parseEnvVar(envVar);
        if (parsed) {
          flags.env[parsed.key] = parsed.value;
        } else {
          warnings.push(`Invalid env format: ${envVar}. Expected KEY=VALUE.`);
        }
        i += 1;
      } else if (arg === '--header' || arg === '-H') {
        // Header flag
        i += 1;
        const header = args[i];
        const parsed = BaseCliParser.parseHeader(header);
        if (parsed) {
          flags.headers[parsed.key] = parsed.value;
        } else {
          warnings.push(`Invalid header format: ${header}. Expected "Name: Value".`);
        }
        i += 1;
      } else if (arg === '--scope' || arg === '-s') {
        // Scope flag (informational only)
        i += 1;
        flags.scope = args[i];
        i += 1;
      } else if (arg.startsWith('--') && !arg.includes('=')) {
        // Unknown flag with value
        const nextArg = args[i + 1];
        // Check if next arg looks like a value (not a flag)
        if (nextArg && !nextArg.startsWith('-')) {
          flags.unknownFlags.push(arg);
          i += 2;
        } else {
          // Flag without value
          flags.unknownFlags.push(arg);
          i += 1;
        }
      } else if (arg.startsWith('-') && arg.length === 2) {
        // Short flag (unknown)
        const nextArg = args[i + 1];
        if (nextArg && !nextArg.startsWith('-')) {
          flags.unknownFlags.push(arg);
          i += 2;
        } else {
          flags.unknownFlags.push(arg);
          i += 1;
        }
      } else {
        // Positional argument
        positionalArgs.push(arg);
        i += 1;
      }
    }

    // Process positional arguments
    // Format: <name> [url]
    // OR: <name> (with -- <command> [args...] handled above)
    const [name, secondArg, ...restArgs] = positionalArgs;
    if (name !== undefined) {
      flags.name = name;

      // Second positional arg could be a URL
      if (secondArg !== undefined) {
        if (BaseCliParser.isUrl(secondArg)) {
          flags.url = secondArg;
        } else if (!sawDoubleDash) {
          // Not a URL and no --, treat as command
          flags.command = secondArg;
          flags.args = restArgs;
        }
      }
    }

    return flags;
  }
}

// Export singleton instance
export const claudeParser = new ClaudeCliParser();
